import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { readCookie } from '../../utils/cookie';
import { fetcher } from '../../utils/fetcher';

import { CoreUIDataContext } from './context';
import { VALID_ENVS, VALID_PLATFORMS } from './types';
import { resolveEndpoint } from './url-table';

import type {
  CoreUIDataContextValue,
  CoreUIDataProviderProps,
  DomainKey,
  DomainResponseMap,
  EntryState,
  FetchEntryOptions,
  FetchError,
  Platform,
} from './types';
import type { FetchResult } from '../../utils/fetcher';

/**
 * 도메인별 visibility refetch 정책. `document.visibilityState === 'visible'` 로 전환 시 자동 refetch.
 *
 * 호스트가 페이지 떠난 사이 상태가 stale 됐을 user-specific 도메인만 등록.
 * `category` / `bi` 같은 static-shape 도메인은 미등록 (불필요).
 *
 * @see plan/cart-and-fetcher-override-design.md — §6-2 (Q3 γ 정책)
 */
const REFETCH_ON_VISIBLE: ReadonlySet<DomainKey> = new Set<DomainKey>(['cart']);

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

/**
 * Core-UI 데이터 Provider — env 기반 lazy fetch + host fetcher override + Provider-scoped store + state machine.
 *
 * # 책임
 *
 * - 환경(env) 별 URL 매핑 (url-table 참조)
 * - `fetchers` prop 으로 host fetcher override (auth-required user-specific 도메인 지원)
 * - 도메인별 entry state machine: `idle → loading → (success | error)`
 * - widget hook (`useCoreUIDataEntry`) 으로 자동 fetch 트리거 + entry 구독
 * - mount-time fetch dedup (force=false), 명시적 invalidate (force=true) 는 in-flight abort 후 재요청
 * - `useCoreUIDataRefetch` hook 으로 같은 tab 내 명시적 invalidate (add-to-cart 후 등)
 * - tab visibility 복귀 (`visibilitychange → visible`) 시 user-specific 도메인 (`REFETCH_ON_VISIBLE`) 자동 refetch
 * - Provider unmount 시 in-flight abort + StrictMode 안전 (loading entry 를 idle 로 복원)
 *
 * # 호스트 사용
 *
 * ```tsx
 * <CoreUIDataProvider
 *   env="prod"
 *   platform={platform} // 'Android' | 'iOS' | 'Web' | 'MWeb'
 *   fetchers={{
 *     cart: async (signal) => (await myAuth.get('/cart/has-items', { signal })).data,
 *   }}
 * >
 *   <CategoryWidget onAction={...} />
 *   <Header>...</Header>
 * </CoreUIDataProvider>
 * ```
 *
 * # env prop 정책
 *
 * mount 시점의 env 가 fetch 의 endpoint 결정. **동적 변경 무시** — 변경 시 자동 reset / warning 없음.
 * 호스트가 env 변경 + cache 무효화 원하면 `<Provider key={env}>` 로 명시적 remount.
 *
 * # 미지원 (의도된 design)
 *
 * - Suspense / throw 패턴
 * - prefetch / staleTime / 자동 refetch (도메인별 hardcoded policy 만 — `REFETCH_ON_VISIBLE`)
 * - nested Provider 가드
 *
 * @see plan/category-widget-design.md — Q4-Q26
 * @see plan/cart-and-fetcher-override-design.md — fetcher override + cart + invalidate
 */
export function CoreUIDataProvider({
  env,
  platform,
  onError,
  fetchers,
  children,
}: CoreUIDataProviderProps): React.ReactElement {
  // platform 미지정 시 라이브러리 default 'Web' 합성. BFF 는 헤더 무조건 받음 정책이라
  // 라이브러리가 default 책임지는 게 자연스러움. 트래픽 분석용으로 정확성 요구가 낮아 type-level required 미채택.
  const resolvedPlatform: Platform = platform ?? 'Web';

  if (process.env.NODE_ENV !== 'production') {
    if (!(VALID_ENVS as readonly string[]).includes(env)) {
      // eslint-disable-next-line no-console
      console.error(
        `[core-ui] CoreUIDataProvider received invalid env: "${env}". Expected one of: ${VALID_ENVS.join(' | ')}.`,
      );
    }
    // platform 은 명시했을 때만 값 검증. undefined 는 정상 (default 적용 경로).
    if (platform !== undefined && !(VALID_PLATFORMS as readonly string[]).includes(platform)) {
      // eslint-disable-next-line no-console
      console.error(
        `[core-ui] CoreUIDataProvider received invalid platform: "${platform}". Expected one of: ${VALID_PLATFORMS.join(
          ' | ',
        )}.`,
      );
    }
  }

  // dev nudge — platform 미지정 시 mount 1회 콘솔 안내. 모바일 호스트의 *silent inaccuracy*
  // (Web 으로 잘못 카운트) 를 인지할 기회 제공. prod 는 silent.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (platform === undefined) {
      // eslint-disable-next-line no-console
      console.warn(
        "[core-ui] CoreUIDataProvider: platform 미지정 — 'Web' 으로 합성 중. " +
          '정확한 트래픽 통계 위해 명시 권장 (모바일 호스트는 platform="MWeb").',
      );
    }
    // mount 1회 nudge — render 마다 발화 방지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [entries, setEntries] = useState<Partial<Record<DomainKey, EntryState>>>({});
  const abortersRef = useRef<Map<DomainKey, AbortController>>(new Map());
  // explicit type — useRef 의 추론이 IDE 환경에 따라 any 로 fallback 되는 케이스 회피.
  // .current?.(...) 호출이 unsafe call 로 잡히지 않도록.
  const onErrorRef = useRef<CoreUIDataProviderProps['onError']>(onError);
  onErrorRef.current = onError;

  // entries 의 동기 ref — hook 안에서 stale closure 없이 dedup 검사용
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // fetchers 의 동기 ref — 호스트가 useCallback 안 해도 latest fetcher 사용.
  const fetchersRef = useRef(fetchers);
  fetchersRef.current = fetchers;

  const fetchEntry = useCallback(
    (key: DomainKey, options: FetchEntryOptions = {}) => {
      const { force = false } = options;

      // in-flight 처리
      // - force=false (default): 이미 in-flight 면 skip (mount-time race dedup)
      // - force=true: 기존 요청 abort 후 새 fetch 시작 (명시적 invalidate trigger)
      const inFlight = abortersRef.current.get(key);
      if (inFlight !== undefined) {
        if (force) {
          inFlight.abort();
          abortersRef.current.delete(key);
        } else {
          return;
        }
      }

      const controller = new AbortController();
      abortersRef.current.set(key, controller);
      setEntries((prev) => ({ ...prev, [key]: { status: 'loading' } }));

      // host fetcher override 가 있으면 그것 사용, 없으면 url-table 의 env-based fetcher.
      const customFetcher = fetchersRef.current?.[key];
      const promise: Promise<FetchResult<unknown>> = customFetcher
        ? (async (): Promise<FetchResult<unknown>> => {
            try {
              const data = await customFetcher(controller.signal);
              return { kind: 'success', data };
            } catch (rawError) {
              if (controller.signal.aborted) return { kind: 'abort' };
              return { kind: 'error', error: { kind: 'network', cause: toError(rawError) } };
            }
          })()
        : (() => {
            const config = resolveEndpoint(key, env);
            if (config === undefined) {
              const error: FetchError = {
                kind: 'network',
                cause: new Error(`[core-ui] No endpoint configured for domain="${key}" env="${env}".`),
              };
              return Promise.resolve<FetchResult<unknown>>({ kind: 'error', error });
            }
            // url-table 기반 fetch 의 자동 헤더 합성:
            // - `Nol-Platform`: 호스트 prop (미지정 시 'Web')
            // - `cgntId`: 브라우저 cookie 에서 추출 (anonymous 환경에선 cookie 없으면 헤더 생략).
            //   `.yanolja.com` 도메인 cookie 라 cross-subdomain 자동 동행되긴 하지만,
            //   *cross-origin BFF + credentials:'same-origin'* 환경에서 명시적 헤더 전송이 정공법.
            // host fetcher override 는 호스트가 자체 environment 에서 직접 헤더 처리 (Q4 정책).
            const cgntId = readCookie('cgntId');
            const mergedHeaders: Record<string, string> = {
              ...config.headers,
              'Nol-Platform': resolvedPlatform,
            };
            if (cgntId !== undefined) {
              mergedHeaders.cognitoid = cgntId;
            }
            const configWithHeaders: typeof config = { ...config, headers: mergedHeaders };
            return fetcher(configWithHeaders, controller.signal);
          })();

      void promise.then((result) => {
        // 더 새로운 controller 가 등록되었거나 unmount 로 clear 된 경우 — 결과 무시
        if (abortersRef.current.get(key) !== controller) return;
        abortersRef.current.delete(key);

        if (result.kind === 'abort') return;
        if (result.kind === 'success') {
          setEntries((prev) => ({ ...prev, [key]: { status: 'success', data: result.data } }));
        } else {
          setEntries((prev) => ({ ...prev, [key]: { status: 'error', error: result.error } }));
          onErrorRef.current?.(key, result.error);
        }
      });
    },
    // platform 도 env 와 동일하게 mount 시점 값만 의미 (변경 무시 정책). deps 에 포함해서
    // 호스트가 platform 변경하면 fetchEntry reference 갱신은 되지만 이미 fetch 된 도메인은 idle 이 아니라
    // 다시 fetch 안 일어남 — `<Provider key={platform}>` 로 명시적 remount 가 의도된 경로.
    [env, resolvedPlatform],
  );

  // unmount cleanup: in-flight abort + StrictMode 안전 (loading entry 를 idle 로 복원해서 remount 시
  // hook 의 ensureFetched 가 새 fetch 시작 가능하게).
  useEffect(() => {
    const aborters = abortersRef.current;
    return () => {
      aborters.forEach((c) => {
        c.abort();
      });
      aborters.clear();

      setEntries((prev) => {
        let changed = false;
        const next = { ...prev };

        for (const key of Object.keys(prev) as DomainKey[]) {
          if (prev[key]?.status === 'loading') {
            next[key] = { status: 'idle' };
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
  }, []);

  // visibility refetch — 탭 복귀 (visibilitychange → visible) 시 user-specific 도메인 자동 갱신.
  // - 'hidden' 전환은 ignore — 떠날 때는 fetch 안 함
  // - 'visible' 전환만 trigger — 사용자가 페이지로 돌아왔을 때 stale 상태 갱신
  // - 한 번이라도 mount 된 도메인 (entries 에 키 존재) 만 refetch → CartAction 없는 페이지는 no-op
  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') return;
      REFETCH_ON_VISIBLE.forEach((key) => {
        if (entriesRef.current[key] !== undefined) {
          // visible 복귀 — 떠나 있는 동안 상태 변경됐을 수 있음. force 로 in-flight 우회 + 새 요청.
          fetchEntry(key, { force: true });
        }
      });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchEntry]);

  const value = useMemo<CoreUIDataContextValue>(
    () => ({ env, entries, entriesRef, fetchEntry }),
    [env, entries, fetchEntry],
  );

  return <CoreUIDataContext.Provider value={value}>{children}</CoreUIDataContext.Provider>;
}

/**
 * Widget 내부에서 도메인 데이터 구독 + 자동 fetch.
 *
 * React-Query 스타일 — mount 시 자동 fetch (idle 일 때만, dedup). entry 변경 시 widget re-render.
 *
 * # options.enabled
 *
 * - `true` (default): 자동 fetch + entry 구독
 * - `false`: fetch trigger skip, idle entry 반환. host 가 다른 source (예: `data` prop) 우선 사용 시
 *
 * # Provider 부재
 *
 * - `enabled: true` + Provider null → idle entry + dev warn (조용히 동작, host 가 fallback 처리 가능)
 * - `enabled: false` + Provider null → idle entry, warn 없음 (의도된 사용 — host 가 data prop 등으로 분기)
 *
 * 반환:
 * - `entry`: 현재 도메인 상태 (`idle | loading | success | error`)
 * - `retry`: 강제 재 fetch. Provider 없거나 enabled false 면 no-op
 */
export function useCoreUIDataEntry<K extends DomainKey>(
  key: K,
  options: { enabled?: boolean } = {},
): {
  entry: EntryState<DomainResponseMap[K]>;
  retry: () => void;
} {
  const { enabled = true } = options;
  const ctx = useContext(CoreUIDataContext);

  if (process.env.NODE_ENV !== 'production' && enabled && ctx === null) {
    // eslint-disable-next-line no-console
    console.warn(
      '[core-ui] useCoreUIDataEntry called outside <CoreUIDataProvider>. ' +
        'Wrap the widget in <CoreUIDataProvider> or pass `enabled: false`.',
    );
  }

  useEffect(() => {
    if (!enabled || ctx === null) return;
    if ((ctx.entriesRef.current[key] ?? { status: 'idle' }).status === 'idle') {
      ctx.fetchEntry(key);
    }
  }, [ctx, key, enabled]);

  // entry.data 는 DomainResponseMap[K] 로 narrow.
  // map-response 가드 (mapCartResponse 등) 는 여전히 필수 — 서버가 schema 위반 응답 보낼 가능성.
  const entry = (ctx?.entries[key] ?? { status: 'idle' }) as EntryState<DomainResponseMap[K]>;
  const retry = useCallback(() => {
    // retry 는 사용자 의도의 명시적 invalidate — loading 중이라도 새 fetch 시작.
    // (force=true 로 in-flight dedup 우회. add-to-cart 후 race 시나리오 등)
    ctx?.fetchEntry(key, { force: true });
  }, [ctx, key]);

  return { entry, retry };
}

/**
 * 도메인의 명시적 invalidate trigger. fire-and-forget.
 *
 * # 사용 시나리오
 *
 * - **same-tab 액션 후 refetch** — add-to-cart 직후 cart dot 갱신 등
 * - **호스트가 자체 이벤트로 invalidate** — 사용자 로그인/로그아웃 후 cart 재조회 등
 *
 * `visibilitychange` (탭 복귀) 는 Provider 가 내부적으로 처리하므로 호스트가 직접 호출할 필요 없음.
 *
 * # 동작
 *
 * - `force: true` 로 `fetchEntry` 호출 → in-flight 가 있어도 abort 후 새 fetch 시작.
 * - fetch 완료 await 안 함 — 호스트의 핸들러 (예: add-to-cart) 와 책임 분리.
 * - Provider 부재 시 no-op + dev warn.
 *
 * # 사용
 *
 * ```tsx
 * const refetch = useCoreUIDataRefetch();
 * const onAddToCart = async () => {
 *   await myApi.addToCart(itemId);
 *   refetch('cart'); // 같은 tab dot 갱신
 * };
 * ```
 *
 * @see plan/cart-and-fetcher-override-design.md — §5-3
 */
export function useCoreUIDataRefetch(): (key: DomainKey) => void {
  const ctx = useContext(CoreUIDataContext);
  return useCallback(
    (key: DomainKey) => {
      if (ctx === null) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            '[core-ui] useCoreUIDataRefetch called outside <CoreUIDataProvider> — no-op. ' +
              'Wrap the host tree in <CoreUIDataProvider> to enable refetch.',
          );
        }
        return;
      }
      ctx.fetchEntry(key, { force: true });
    },
    [ctx],
  );
}
